import { useMemo, useState } from 'react';
import { estoqueApi, type EstoqueResumo, type TipoMovimentacaoEstoque } from '@/features/estoque/api/estoqueApi';
import { useEstoque } from '@/features/estoque/hooks/useEstoque';
import { useRotaEntidade } from '@/app/useRotaEntidade';
import { ROTAS } from '@/app/rotas';
import { BadgeSyncPendente } from '@/shared/ui/BadgeSyncPendente';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { IconInbox, IconMinus, IconPlus, IconRefresh } from '@/shared/ui/Icon';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Paginacao } from '@/shared/ui/Paginacao';
import { SearchInput, normalizarBusca } from '@/shared/ui/SearchInput';
import { usePaginacao } from '@/shared/hooks/usePaginacao';

type ModoMovimento =
  | { tipo: 'fechado' }
  | { tipo: TipoMovimentacaoEstoque; item: EstoqueResumo };

const formatadorData = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
});

function formatarQuantidade(valor: number): string {
  return Math.trunc(valor).toLocaleString('pt-BR');
}

function inteiroPositivo(valor: string): number {
  return Math.max(0, Math.trunc(Number(valor) || 0));
}

export function EstoquePage() {
  const { itens, movimentacoes, carregando, erro, recarregar } = useEstoque();
  const [busca, setBusca] = useState('');
  // Entrada e saída têm endereço próprio: `/estoque/:produtoId/entrada`
  // e `/estoque/:produtoId/saida`.
  const rota = useRotaEntidade(ROTAS.estoque);

  const modo = useMemo<ModoMovimento>(() => {
    if (rota.modo !== 'acao') return { tipo: 'fechado' };
    const item = itens.find((i) => i.produtoId === rota.id);
    if (!item) return { tipo: 'fechado' };
    if (rota.acao === 'entrada') return { tipo: 'Entrada', item };
    if (rota.acao === 'saida') return { tipo: 'Saida', item };
    return { tipo: 'fechado' };
  }, [rota, itens]);

  const filtrados = useMemo(() => {
    const q = normalizarBusca(busca.trim());
    if (!q) return itens;
    return itens.filter((i) => normalizarBusca(`${i.codigo} ${i.nome}`).includes(q));
  }, [itens, busca]);

  const movimentacoesFiltradas = useMemo(() => {
    const ids = new Set(filtrados.map((i) => i.produtoId));
    return movimentacoes.filter((m) => ids.has(m.produtoId));
  }, [movimentacoes, filtrados]);

  const saldos = usePaginacao(filtrados, 25);
  const movs = usePaginacao(movimentacoesFiltradas, 25);

  async function aoSalvarMovimento(dados: { quantidade: number; motivo: string }) {
    if (modo.tipo === 'fechado') return;
    if (modo.tipo === 'Entrada') {
      await estoqueApi.adicionar(modo.item.produtoId, dados);
    } else {
      await estoqueApi.remover(modo.item.produtoId, dados);
    }
    rota.fechar();
    await recarregar();
  }

  return (
    <section className="page">
      <PageHeader
        titulo="Estoque"
        descricao="Saldos atuais e movimentações de entrada e saída."
        acoes={
          <Button onClick={() => void recarregar()} disabled={carregando}>
            <IconRefresh className="btn__icon" />
            {carregando ? 'Atualizando...' : 'Atualizar'}
          </Button>
        }
      />

      {erro && (
        <p role="alert" className="alert alert--error">
          Falha ao carregar: {erro}
        </p>
      )}

      <div className="card">
        <div className="card__header card__header--com-busca">
          <span className="card__title">
            {filtrados.length} de {itens.length} produto(s)
          </span>
          <SearchInput
            valor={busca}
            onChange={setBusca}
            placeholder="Buscar por código ou nome..."
            ariaLabel="Buscar produto no estoque"
          />
        </div>
        <div className="card__body card__body--flush">
          {itens.length === 0 && !carregando && !erro ? (
            <EmptyState
              icone={<IconInbox />}
              titulo="Nenhum produto cadastrado."
              descricao="Cadastre produtos para controlar o estoque."
            />
          ) : filtrados.length === 0 ? (
            <EmptyState
              icone={<IconInbox />}
              titulo="Nenhum produto corresponde à busca."
              descricao="Tente outro termo ou limpe o campo de busca."
            />
          ) : (
            <div className="table-scroll">
              <table className="table table--responsive">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Produto</th>
                    <th className="table__numeric">Estoque</th>
                    <th>Status</th>
                    <th style={{ width: 1 }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {saldos.paginados.map((i) => (
                    <tr key={i.produtoId}>
                      <td className="table__primary">{i.codigo}</td>
                      <td data-label="Produto">{i.nome}</td>
                      <td className="table__numeric" data-label="Estoque">{formatarQuantidade(Math.max(i.quantidade, 0))}</td>
                      <td data-label="Status">
                        {i.ativo ? (
                          <span className="badge badge--ativo">Ativo</span>
                        ) : (
                          <span className="badge badge--inativo">(inativo)</span>
                        )}
                      </td>
                      <td>
                        <div className="row-actions">
                          <Button
                            className="btn--icon"
                            aria-label={`Adicionar ao estoque de ${i.nome}`}
                            onClick={() => rota.abrir(`${i.produtoId}/entrada`)}
                          >
                            <IconPlus />
                          </Button>
                          <Button
                            className="btn--icon btn--danger"
                            aria-label={`Remover do estoque de ${i.nome}`}
                            onClick={() => rota.abrir(`${i.produtoId}/saida`)}
                          >
                            <IconMinus />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Paginacao
            pagina={saldos.pagina}
            totalPaginas={saldos.totalPaginas}
            total={saldos.total}
            tamanhoPagina={saldos.tamanhoPagina}
            onMudarPagina={saldos.setPagina}
          />
        </div>
      </div>

      <div className="card">
        <div className="card__header">
          <span className="card__title">Movimentações</span>
        </div>
        <div className="card__body card__body--flush">
          {movimentacoesFiltradas.length === 0 ? (
            <EmptyState
              icone={<IconInbox />}
              titulo="Nenhuma movimentação encontrada."
              descricao="Entradas e saídas aparecerão aqui."
            />
          ) : (
            <div className="table-scroll">
              <table className="table table--responsive">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Produto</th>
                    <th>Tipo</th>
                    <th className="table__numeric">Qtd.</th>
                    <th className="table__numeric">Saldo</th>
                    <th>Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {movs.paginados.map((m) => (
                    <tr key={m.id}>
                      <td className="table__primary">{formatadorData.format(new Date(m.criadoEm))}</td>
                      <td data-label="Produto">{m.produtoCodigo} - {m.produtoNome}</td>
                      <td data-label="Tipo">
                        <span className={`badge ${m.tipo === 'Entrada' ? 'badge--ativo' : 'badge--inativo'}`}>
                          {m.tipo === 'Entrada' ? 'Entrada' : 'Saída'}
                        </span>
                      </td>
                      <td className="table__numeric" data-label="Quantidade">{formatarQuantidade(m.quantidade)}</td>
                      <td className="table__numeric" data-label="Saldo">{formatarQuantidade(m.saldoDepois)}</td>
                      <td data-label="Motivo">
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          {m.motivo}
                          <BadgeSyncPendente pendente={m.syncPendente} />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Paginacao
            pagina={movs.pagina}
            totalPaginas={movs.totalPaginas}
            total={movs.total}
            tamanhoPagina={movs.tamanhoPagina}
            onMudarPagina={movs.setPagina}
          />
        </div>
      </div>

      {modo.tipo !== 'fechado' && (
        <MovimentoEstoqueModal
          modo={modo}
          aoFechar={rota.fechar}
          aoSalvar={aoSalvarMovimento}
        />
      )}
    </section>
  );
}

function MovimentoEstoqueModal({
  modo,
  aoFechar,
  aoSalvar,
}: {
  modo: Exclude<ModoMovimento, { tipo: 'fechado' }>;
  aoFechar: () => void;
  aoSalvar: (dados: { quantidade: number; motivo: string }) => Promise<void>;
}) {
  const [quantidade, setQuantidade] = useState(1);
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      await aoSalvar({ quantidade, motivo });
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Falha ao movimentar estoque.');
    } finally {
      setSalvando(false);
    }
  }

  const titulo = modo.tipo === 'Entrada' ? 'Adicionar ao estoque' : 'Remover do estoque';

  return (
    <Modal
      aberto
      titulo={titulo}
      onFechar={aoFechar}
      rodape={
        <>
          <Button onClick={aoFechar} disabled={salvando}>
            Cancelar
          </Button>
          <Button variante="primary" onClick={(e) => void handleSubmit(e as unknown as React.FormEvent)} disabled={salvando}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </>
      }
    >
      <form className="form-grid" onSubmit={handleSubmit}>
        {erro && <div className="form-error">{erro}</div>}

        <Field rotulo="Produto">
          {(id) => (
            <input
              id={id}
              className="input"
              value={`${modo.item.codigo} - ${modo.item.nome}`}
              readOnly
              disabled
            />
          )}
        </Field>

        <Field rotulo="Quantidade" obrigatorio>
          {(id) => (
            <input
              id={id}
              className="input"
              type="number"
              min="1"
              step="1"
              value={quantidade}
              onChange={(e) => setQuantidade(inteiroPositivo(e.target.value))}
              required
            />
          )}
        </Field>

        <Field rotulo="Motivo" obrigatorio>
          {(id) => (
            <textarea
              id={id}
              className="input"
              rows={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              minLength={2}
              maxLength={500}
              required
            />
          )}
        </Field>
      </form>
    </Modal>
  );
}
